import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { CookieConsentProvider } from "@/hooks/useCookieConsent";
import PageTransition from "@/components/layout/PageTransition";
import ScrollToTop from "@/components/layout/ScrollToTop";
import AdminGuard from "@/components/auth/AdminGuard";
import { GlobalScripts } from "@/components/seo/GlobalScripts";
import { RootErrorBoundary } from "@/components/debug/RootErrorBoundary";
import { CookieBanner, CookieSettingsModal, CookieScriptLoader } from "@/components/cookies";
import { ReadingModeProvider } from "@/components/layout/ReadingModeTransition";

// Eagerly load the landing page for best LCP
import Index from "./pages/Index";

// Lazy load all other pages to reduce initial bundle size
const Login = lazy(() => import("./pages/Login"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateStory = lazy(() => import("./pages/CreateStory"));
const GeneratingView = lazy(() => import("./pages/GeneratingView"));
const StoryReader = lazy(() => import("./pages/StoryReader"));
const SharedStory = lazy(() => import("./pages/SharedStory"));
const Profiles = lazy(() => import("./pages/Profiles"));
const Settings = lazy(() => import("./pages/Settings"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Security = lazy(() => import("./pages/Security"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminUserProfile = lazy(() => import("./pages/AdminUserProfile"));
const AdminBulkCredits = lazy(() => import("./pages/AdminBulkCredits"));
const AdminTransactions = lazy(() => import("./pages/AdminTransactions"));
const AdminStats = lazy(() => import("./pages/AdminStats"));
const AdminPayments = lazy(() => import("./pages/AdminPayments"));
const AdminAccounting = lazy(() => import("./pages/AdminAccounting"));
const AdminSiteManagement = lazy(() => import("./pages/AdminSiteManagement"));
const AdminNews = lazy(() => import("./pages/AdminNews"));
const AdminNewsEditor = lazy(() => import("./pages/AdminNewsEditor"));
const AdminAIModels = lazy(() => import("./pages/AdminAIModels"));
const AdminImageOptimization = lazy(() => import("./pages/AdminImageOptimization"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const NewsPreview = lazy(() => import("./pages/NewsPreview"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Minimal loading fallback that won't cause layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Laddar...</div>
  </div>
);

// Dark loader for story reader to prevent white flash during transition
const DarkPageLoader = () => (
  <div className="min-h-screen" style={{ backgroundColor: '#0E0F12' }} />
);

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/verify-email" element={<PageTransition><VerifyEmail /></PageTransition>} />
          <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
          <Route path="/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
          <Route path="/profiles" element={<PageTransition><Profiles /></PageTransition>} />
          <Route path="/create/:storyId?" element={<PageTransition><CreateStory /></PageTransition>} />
          <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
          <Route path="/cookies" element={<PageTransition><Cookies /></PageTransition>} />
          <Route path="/how-it-works" element={<PageTransition><HowItWorks /></PageTransition>} />
          <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
          <Route path="/security" element={<PageTransition><Security /></PageTransition>} />
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/pricing" element={<PageTransition><Pricing /></PageTransition>} />
          <Route path="/generating/:id" element={<PageTransition><GeneratingView /></PageTransition>} />
          <Route path="/story/:id" element={<Suspense fallback={<DarkPageLoader />}><StoryReader /></Suspense>} />
          <Route path="/shared/:token" element={<PageTransition><SharedStory /></PageTransition>} />
          <Route path="/admin" element={<AdminGuard><PageTransition><Admin /></PageTransition></AdminGuard>} />
          <Route path="/admin/users" element={<AdminGuard><PageTransition><AdminUsers /></PageTransition></AdminGuard>} />
          <Route path="/admin/users/:userId" element={<AdminGuard><PageTransition><AdminUserProfile /></PageTransition></AdminGuard>} />
          <Route path="/admin/credits" element={<AdminGuard><PageTransition><AdminBulkCredits /></PageTransition></AdminGuard>} />
          <Route path="/admin/transactions" element={<AdminGuard><PageTransition><AdminTransactions /></PageTransition></AdminGuard>} />
          <Route path="/admin/stats" element={<AdminGuard><PageTransition><AdminStats /></PageTransition></AdminGuard>} />
          <Route path="/admin/payments/*" element={<AdminGuard><PageTransition><AdminPayments /></PageTransition></AdminGuard>} />
          <Route path="/admin/accounting" element={<AdminGuard><PageTransition><AdminAccounting /></PageTransition></AdminGuard>} />
          <Route path="/admin/site" element={<AdminGuard><PageTransition><AdminSiteManagement /></PageTransition></AdminGuard>} />
          <Route path="/admin/news" element={<AdminGuard><PageTransition><AdminNews /></PageTransition></AdminGuard>} />
          <Route path="/admin/news/new" element={<AdminGuard><PageTransition><AdminNewsEditor /></PageTransition></AdminGuard>} />
          <Route path="/admin/news/edit/:id" element={<AdminGuard><PageTransition><AdminNewsEditor /></PageTransition></AdminGuard>} />
          <Route path="/admin/ai-models" element={<AdminGuard><PageTransition><AdminAIModels /></PageTransition></AdminGuard>} />
          <Route path="/admin/image-optimization" element={<AdminGuard><PageTransition><AdminImageOptimization /></PageTransition></AdminGuard>} />
          <Route path="/news" element={<PageTransition><News /></PageTransition>} />
          <Route path="/news/preview/:id" element={<AdminGuard><PageTransition><NewsPreview /></PageTransition></AdminGuard>} />
          <Route path="/news/:slug" element={<PageTransition><NewsArticle /></PageTransition>} />
          <Route path="/ref/:code" element={<PageTransition><ReferralRedirect /></PageTransition>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => (
  <HelmetProvider>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CookieConsentProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <GlobalScripts />
              <CookieScriptLoader />
              <BrowserRouter>
                <ReadingModeProvider>
                  <ScrollToTop />
                  <AnimatedRoutes />
                  <CookieBanner />
                  <CookieSettingsModal />
                </ReadingModeProvider>
              </BrowserRouter>
            </TooltipProvider>
          </CookieConsentProvider>
        </AuthProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  </HelmetProvider>
);

export default App;
